import {
  Box,
  Paper,
  useTheme,
  Tooltip,
  Typography,
  alpha,
  TextField,
  InputAdornment,
} from "@mui/material";
import { RepeatProps } from "./types";
import { useState, useMemo, useEffect, useCallback } from "react";
import { Responsive, WidthProvider, Layouts, Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import TemplateCard from "../../../pages/template/templateCard";
import { useTemplate } from "../../../context/templateContext";
import AddIcon from "@mui/icons-material/Add";
import { Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { useFilters } from "../../../context/filterContext";

const ResponsiveGridLayout = WidthProvider(Responsive);

const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const cols = { lg: 50, md: 24, sm: 12, xs: 8, xxs: 4 };
const rowHeight = 50;

const Repeat: React.FC<RepeatProps> = ({
  config,
  updateCard,
  deleteCard,
  duplicateCard,
  id,
  selectedFilter,
  paginaId,
  isCardLoading,
  firstLoading,
  filtersInitialized,
  loading,
  ...props
}) => {
  const [selectedCard, setSelectedCard] = useState<string | null>();
  const [searchText, setSearchText] = useState("");
  const [hoveredInnerCardId, setHoveredInnerCardId] = useState<string | null>(
    null,
  );
  const { filters, setFilters, handleApiFilter } = useFilters();
  const theme = useTheme();
  const navigate = useNavigate();
  const { editMode, onAddCard, setEditMode, onSave } = useTemplate();
  const graphics = (props.graphics as DashCardsGraphics[]) || [];

  const handleRemoveCard = useCallback((id: string) => {
    deleteCard(id);
  }, []);

  const layouts: Layouts = useMemo(() => {
    const layoutItems = graphics.map((g) => ({
      i: g.id,
      x: g.positionLayout?.x ?? 0,
      y: g.positionLayout?.y ?? 0,
      w: g.positionLayout?.w ?? 10,
      h: g.positionLayout?.h ?? 6,
      minW: 2,
      minH: 2,
      maxW: 50,
      maxH: 50,
    }));

    if (editMode) {
      const maxY = layoutItems.reduce(
        (max, item) => Math.max(max, (item.y || 0) + (item.h || 0)),
        0,
      );
      layoutItems.push({
        i: "add-card",
        x: 0,
        y: maxY,
        w: 6,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 50,
        maxH: 50,
      });
    }

    return {
      lg: layoutItems,
      md: layoutItems,
      sm: layoutItems,
      xs: layoutItems,
      xxs: layoutItems,
    };
  }, [graphics, editMode]);

  const handleDragStop = (layout: Layout[]) => {
    if (!updateCard) return;
    layout.forEach((item) => {
      const graphic = graphics.find((g) => g.id === item.i);
      if (!graphic) return;
      const updatedGraphic = {
        ...graphic,
        positionLayout: {
          x: item.x,
          y: item.y,
          w: item.w,
          h: item.h,
        },
      };
      updateCard(updatedGraphic);
    });
  };

  const handleAddGraph = () => {
    const maxY = graphics.reduce((max: number, card: DashCardsGraphics) => {
      const y: number = card.positionLayout?.y || 0;
      const h: number = card.positionLayout?.h || 0;
      return Math.max(max, y + h);
    }, 0);
    if (onAddCard) {
      onAddCard(id, maxY);
    }
    return;
  };

  const filteredItems = useMemo(() => {
    return selectedFilter && selectedFilter?.options
      ? selectedFilter.options.filter((item) =>
          item.toLowerCase().includes(searchText.toLowerCase()),
        )
      : [];
  }, [searchText, selectedFilter]);

  const handleSelectCard = (item: string) => {
    const currentFilters = JSON.parse(localStorage.getItem("filters") || "{}");

    setSelectedCard(item);
    if (selectedFilter?.variable) {
      const newFilters = {
        ...filters,
        [selectedFilter.variable]: [item],
      };

      setFilters(newFilters);
      localStorage.setItem(
        "filters",
        JSON.stringify({ ...currentFilters, [String(paginaId)]: newFilters }),
      );
      if (handleApiFilter) {
        handleApiFilter(newFilters);
      }
    }
  };

  useEffect(() => {
    const currentFilters =
      filters && Object.keys(filters).length > 0
        ? filters
        : JSON.parse(localStorage.getItem("filters") || "{}")[String(paginaId)];

    const activeFilters = currentFilters?.[selectedFilter?.variable || ""];

    if (Array.isArray(activeFilters) && activeFilters.length === 1) {
      setSelectedCard(activeFilters[0]);
    } else {
      setSelectedCard(null);
    }
  }, [paginaId, selectedFilter, filters]);

  // Hotkeys
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!hoveredInnerCardId) return;

      const isHotkey =
        event.key === "e" ||
        (event.ctrlKey &&
          (event.key === "r" || event.key === "d" || event.key === "s"));

      if (isHotkey) {
        // Stop propagation in the capture phase to prevent the parent grid (bubbling listener) from receiving the event
        event.stopPropagation();
      }

      if (event.key === "e") {
        event.preventDefault();
        setEditMode(true);
        navigate(`${window.location.pathname}/${hoveredInnerCardId}`);
        return;
      }

      if (!editMode) return;

      // Ctrl+R to remove
      if (event.ctrlKey && event.key === "r") {
        event.preventDefault();
        handleRemoveCard(hoveredInnerCardId);
        return;
      }

      // Ctrl+D to duplicate
      if (event.ctrlKey && event.key === "d") {
        event.preventDefault();
        console.log("Duplicate inner card", hoveredInnerCardId);
        return;
      }

      // Ctrl+S to save
      if (event.ctrlKey && event.key === "s") {
        event.preventDefault();
        if (onSave) onSave();
        return;
      }
    };

    // Use capture: true so this runs before the parent's bubbling listener
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    hoveredInnerCardId,
    editMode,
    onSave,
    handleRemoveCard,
    setEditMode,
    navigate,
  ]);
  const filteredLayouts = Object.keys(layouts).reduce(
    (acc: any, breakpoint) => {
      acc[breakpoint] = layouts[breakpoint].map((item) => ({
        ...item,
        isResizable: item.i === "add-card" ? false : editMode,
      }));
      return acc;
    },
    {},
  );
  const styles = {
    boxContainer: {
      display: "flex",
      marginY: 3,
      borderRadius: 2,
      overflow: "hidden",
    },
    paper: {
      flex: 1,
      width: "100%",
      overflowX: "hidden",
      padding: 2,
      overflowY: "auto",
      gap: 2,
      backgroundColor: "none",
    },
    boxCards: {
      display: "flex",
      flexDirection: "row",
      padding: 1,
      mb: 1.5,
      height: "100%",
      maxHeight: 50,
      alignItems: "center",
      justifyContent: "space-between",
      borderRadius: "5px",
      backgroundColor: theme.palette.background.card,
      overflow: "hidden",
      width: "100%",
    },
    boxDetail: {
      height: "100%",
      width: "5px",
      borderRadius: "18px",
      mr: 2,
    },
    typography: {
      fontSize: "1rem",
      fontWeight: 500,
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      flex: 1,
      maxWidth: "100%",
    },
    boxReactGrid: {
      flex: 3,
      p: 2,
      m: 1,
      width: "100%",
      overflowY: "auto",
      backgroundColor: theme.palette.background.default,
    },
    boxGraphic: {
      width: "100%",
      height: "100%",
      borderRadius: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    },
    boxAddGraphic: {
      width: "100%",
      height: "100%",
      borderRadius: 2,
      boxShadow: 2,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      border: `2px dashed ${theme.palette.primary.main}`,
      color: theme.palette.primary.main,
      transition: "background 0.2s",
      "&:hover": {
        bgcolor: theme.palette.action.hover,
      },
    },
  };
  return (
    <Box sx={styles.boxContainer}>
      <Paper elevation={4} sx={styles.paper} className="cancelSelectorName">
        <TextField
          size="small"
          variant="filled"
          value={searchText}
          label="Pesquisar item"
          sx={{ mb: 2 }}
          onChange={(e) => setSearchText(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
        />
        {filteredItems?.map((item, index) => (
          <Box
            key={index}
            sx={{
              ...styles.boxCards,
              border:
                selectedCard === item
                  ? "2px solid " + theme.palette.primary.main
                  : "none",
              "&:hover": {
                backgroundColor:
                  selectedCard === item
                    ? alpha(theme.palette.primary.main, 0.07)
                    : alpha(theme.palette.text.secondary, 0.05),
                cursor: "pointer",
              },
            }}
            onClick={() => handleSelectCard(item)}
          >
            <Box
              sx={{
                ...styles.boxDetail,
                background:
                  selectedCard === item
                    ? theme.palette.primary.main
                    : theme.palette.background.textField,
              }}
            />
            <Tooltip title={item}>
              <Typography sx={styles.typography}>{item}</Typography>
            </Tooltip>
          </Box>
        ))}
      </Paper>
      <Box sx={styles.boxReactGrid}>
        <ResponsiveGridLayout
          className="layout"
          layouts={filteredLayouts}
          breakpoints={breakpoints}
          cols={cols}
          rowHeight={rowHeight}
          isDraggable={editMode}
          isResizable={editMode}
          margin={[5, 5]}
          containerPadding={[0, 0]}
          onDragStop={handleDragStop}
          onResizeStop={handleDragStop}
          draggableHandle=".inner-drag-handle"
        >
          {graphics.map((graphic, index) => (
            <Box
              key={graphic.id}
              sx={styles.boxGraphic}
              className="inner-drag-handle cancelSelectorName"
              onMouseEnter={() => setHoveredInnerCardId(graphic.id || null)}
              onMouseLeave={() => setHoveredInnerCardId(null)}
            >
              <TemplateCard
                index={index}
                card={graphic}
                editMode={editMode}
                isEditing={false}
                handleDelete={handleRemoveCard}
                firstLoading={firstLoading}
                isCardLoading={loading}
                updateCard={updateCard}
                duplicateCard={duplicateCard}
                paginaId={paginaId}
                filtersInitialized={filtersInitialized}
              />
            </Box>
          ))}
          {editMode && (
            <Box
              key="add-card"
              sx={styles.boxAddGraphic}
              onClick={handleAddGraph}
              className="cancelSelectorName"
            >
              <AddIcon fontSize="large" />
              <Box>Adicionar gráfico</Box>
            </Box>
          )}
        </ResponsiveGridLayout>
      </Box>
    </Box>
  );
};

export default Repeat;
